import React from 'react';

import { FlexBox } from 'ml-rubiks-styleguide/src/box';
import { Button } from 'ml-rubiks-styleguide/src/button';
import { CircularProgress } from 'ml-rubiks-styleguide/src/circular-progress';
import {
    IconAdd,
    IconCheckCircle
} from 'ml-rubiks-styleguide/src/icon';
import { List, ListItemProps } from 'ml-rubiks-styleguide/src/list';
import { Paper } from 'ml-rubiks-styleguide/src/paper';
import { Typography } from 'ml-rubiks-styleguide/src/typography';

import { useI18n } from '../../contexts/i18n-context';
import { SavedPaymentMethodListProps } from '../../types';
import { getSavedPaymentMethodDisplayInfo } from '../../utils';
import { LoadingMessage } from '../common';

export const SavedPaymentMethodList: React.FC<SavedPaymentMethodListProps> = ({
    methods,
    selectedMethod,
    onMethodSelect,
    isPaymentLoading = false,
    isLoadingAddNew = false,
    processingMethodId = null,
    showAddButton = false
}) => {
    const { t } = useI18n();

    return (
        <FlexBox stack gap={2}>
            <Paper elevation="S">
                <List
                    disablePadding
                    items={methods.map((method, index) => {
                        const displayInfo = getSavedPaymentMethodDisplayInfo(
                            method,
                            t
                        );
                        return {
                            sx: {
                                height: 68
                            },
                            selected: selectedMethod === method.id,
                            primary: (
                                <FlexBox
                                    component="span"
                                    gap={2}
                                    vAlign="center"
                                    sx={{ width: '100%' }}
                                >
                                    <FlexBox sx={{ minWidth: 35 }}>
                                        {displayInfo.icon}
                                    </FlexBox>
                                    <FlexBox stack>
                                        <Typography>
                                            {displayInfo.title}
                                        </Typography>
                                        {displayInfo.subtitle && (
                                            <Typography color="text.secondary"
                                            >
                                                {displayInfo.subtitle}
                                            </Typography>
                                        )}
                                    </FlexBox>
                                </FlexBox>
                            ),
                            primaryRight:
                                selectedMethod === method.id ? (
                                    <FlexBox hAlign="flex-end">
                                        <IconCheckCircle color="primary.main" />
                                    </FlexBox>
                                ) : (
                                    processingMethodId === method.id && (
                                        <FlexBox hAlign="flex-end">
                                            <CircularProgress size={24} />
                                        </FlexBox>
                                    )
                                ),
                            divider:
                                index === methods.length - 1
                                    ? undefined
                                    : 'bottom',
                            textSlotProps: {
                                primary: {
                                    sx: { fontWeight: 700 }
                                },
                                secondary: {
                                    color: 'text.secondary'
                                }
                            },
                            onClick: () => onMethodSelect(method.id)
                        } satisfies ListItemProps<unknown>;
                    })}
                />
            </Paper>

            {showAddButton && (
                <Button
                    variant="outlined"
                    onClick={() => onMethodSelect('add-new')}
                    disabled={isPaymentLoading || isLoadingAddNew}
                    loading={isLoadingAddNew}
                    fullWidth
                    type="button"
                    aria-label={t(
                        'upc.payment.methods.add.new',
                        'Add new payment method'
                    )}
                >
                    {isLoadingAddNew ? (
                        <LoadingMessage
                            message={t(
                                'upc.loading.creating.payment.request',
                                'Creating payment request...'
                            )}
                        />
                    ) : (
                        <>
                            <IconAdd
                                color="primary.main"
                                sx={{ width: 20, height: 20, mr: 1 }}
                            />
                            {t(
                                'upc.payment.methods.add.new',
                                'Add New Payment Method'
                            )}
                        </>
                    )}
                </Button>
            )}
        </FlexBox>
    );
};
